"use client";

import * as React from "react";
import { DayPicker } from "react-day-picker";
import { enUS } from "date-fns/locale/en-US";
import { uk } from "date-fns/locale/uk";
import { useUiControlLabels } from "@/components/i18n/ui-control-labels-context";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({ className, classNames, locale, ...props }: CalendarProps) {
  const controls = useUiControlLabels()
  return (
    <DayPicker
      locale={locale ?? (controls.locale === "uk" ? uk : enUS)}
      // Render a constant 6 weeks (padded with adjacent-month days) so the
      // grid height never changes between months — otherwise the popover
      // re-positions on every nav click. `fixedWeeks` only takes effect
      // together with `showOutsideDays`.
      fixedWeeks
      showOutsideDays
      className={cn("p-3", className)}
      classNames={{
        months: "relative flex flex-col sm:flex-row gap-2",
        month: "flex flex-col gap-4",
        month_caption: "flex justify-center pt-1 pb-2 items-center h-9",
        caption_label: "text-sm font-medium",
        // `Nav` is a sibling of the month grid, so overlay it on the caption
        // row — otherwise absolutely-positioned buttons escape to the popover.
        nav: "absolute inset-x-0 top-0 z-10 flex items-center justify-between h-9",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "h-9 w-9 p-0 bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary [&_svg]:size-5",
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "h-9 w-9 p-0 bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary [&_svg]:size-5",
        ),
        month_grid: "w-full border-collapse space-y-1",
        weekdays: "flex",
        weekday:
          "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        week: "flex w-full mt-2",
        day: cn(
          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50",
          props.mode === "range"
            ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
            : "[&:has([aria-selected])]:rounded-md",
        ),
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 font-normal aria-selected:opacity-100",
        ),
        range_start: "day-range-start",
        range_end: "day-range-end",
        selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        today: "bg-accent text-accent-foreground",
        outside:
          "day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
        disabled: "text-muted-foreground opacity-50",
        range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) =>
          orientation === "left" ? (
            <ChevronLeft className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          ),
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
